// @ts-check
import React from 'react';

import Tab from '@liquidweb-dev/undercurrent/Tab';
import Tabs from '@liquidweb-dev/undercurrent/Tabs';
import FullScreenError from 'components/molecules/FullScreenError';
import Attributes from 'components/standard/HostingDetailsAttributes';
import Description from 'components/standard/HostingDetailsDescription';
import PageLayout, {
	PageLayoutInsetContent,
} from 'components/standard/PageLayout';
import TabBar from 'components/standard/TabBar';
import hostingDetailsAlsowith from 'containers/pages/servers/details/hostingDetailsAlsowith';
import useAssetDetails from 'modules/queries/asset/useDetails';
import NotFound from 'pages/error/NotFound';
import {
	Redirect,
	Link as RouterLink,
	useLocation,
	useParams,
} from 'react-router-dom';
import { getErrorStringHelper } from 'utility/redux/selectorHelperFunctions/apiModules';
import Actions from './Actions';

/**
 * A layout for VMware.vCloud.vDC servers' details page.
 * @param {object} props
 * @param {React.ReactNode} props.children
 */
const VmWareVdcLayout = ({ children }) => {
	/** @type {{uniqId: string}} */
	const { uniqId } = useParams();
	const { pathname } = useLocation();

	const {
		data: assetDetailsData,
		isLoading: assetDetailsIsLoading,
		isError: assetDetailsIsError,
		error: assetDetailsError,
	} = useAssetDetails({
		uniq_id: uniqId,
		alsowith: hostingDetailsAlsowith,
	});

	if (assetDetailsIsError && assetDetailsError.statusCode === 404) {
		return <NotFound />;
	}

	if (assetDetailsIsError) {
		return (
			<FullScreenError content={getErrorStringHelper(assetDetailsIsError)} />
		);
	}

	const {
		custom_name: customName,
		custom_description: customDescription,
		domain,
		isVmware,
	} = assetDetailsData || {};

	// redirect non VMware.vCloud.vDC servers
	if (isVmware === false) {
		return <Redirect to={`/servers/${uniqId}`} />;
	}

	// the tabs correspond to the last portion of the path after 'vmware-vdc'
	const tabsValue = pathname.split('/')[4] || 'overview';

	const title = customName || domain || '';
	// prevent extra gap in PageLayout
	const shouldRenderDescription =
		Boolean(customDescription) || !(title === domain);

	return (
		<PageLayout
			title={title}
			attributesSlot={<Attributes />}
			actionsSlot={<Actions />}
			isLoading={assetDetailsIsLoading}
			description={
				shouldRenderDescription && (
					<Description
						serverDescription={customDescription}
						// don't show the domain if it's the same as the title
						domain={domain !== title ? domain : null}
					/>
				)
			}
			variant="dynamic"
		>
			<TabBar sx={{ marginBottom: 600 }}>
				<Tabs value={tabsValue} variant="scrollable">
					<Tab
						component={RouterLink}
						value="overview"
						to={`/servers/${uniqId}/vmware-vdc`}
						label="Overview"
					/>
					<Tab
						component={RouterLink}
						value="settings"
						to={`/servers/${uniqId}/vmware-vdc/settings`}
						label="Settings"
					/>
				</Tabs>
			</TabBar>
			<PageLayoutInsetContent sx={{ flexGrow: 1 }}>
				{children}
			</PageLayoutInsetContent>
		</PageLayout>
	);
};

export default VmWareVdcLayout;
